import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, UserX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPT_ORDER = ["København", "Aarhus", "Odense", "Aalborg", "Kolding", "Ukendt"];

function fmt(n) {
  return new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(n);
}

export function LtvView({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/ltv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Kunne ikke hente LTV data"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="text-muted-foreground text-sm py-8 text-center">Henter LTV data...</div>
  );
  if (error) return (
    <div className="text-destructive text-sm py-8 text-center">{error}</div>
  );
  if (!data) return null;

  const { overall, by_dept, cached_at } = data;
  const sortedDepts = DEPT_ORDER.filter((d) => by_dept[d]);
  const maxAvg = Math.max(...sortedDepts.map((d) => by_dept[d].avg_ltv));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-foreground font-bold text-lg flex items-center gap-2">
            <Users size={18} className="text-[#FF2A6D]" />
            Customer Lifetime Value
          </h2>
          <p className="text-muted-foreground text-[13px] mt-0.5">
            Kunder med besøg efter 01/01/2021 · {fmt(overall.count)} aktive kunder
          </p>
        </div>
        {cached_at && (
          <span className="text-muted-foreground text-[13px]">
            Opdateret {new Date(cached_at).toLocaleDateString("da-DK")}
          </span>
        )}
      </div>

      {/* 2 overordnede kort */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="pt-5 pb-4">
            <div className="text-muted-foreground text-[13px] mb-1">Gns. LTV pr. kunde</div>
            <div className="text-foreground text-2xl font-bold font-kpi">{fmt(overall.avg_ltv)} kr</div>
            <div className="text-muted-foreground text-[13px] mt-1">
              samlet køb / {fmt(overall.count)} kunder
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="pt-5 pb-4">
            <div className="text-muted-foreground text-[13px] mb-1 flex items-center gap-1">
              <UserX size={12} /> Engangskunder
            </div>
            <div className="text-foreground text-2xl font-bold font-kpi">{overall.once_only_pct}%</div>
            <div className="text-muted-foreground text-[13px] mt-1">kun ét besøg</div>
          </CardContent>
        </Card>
      </div>

      {/* Per afdeling */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-foreground text-sm font-semibold">LTV pr. afdeling</CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          <div className="space-y-5">
            {sortedDepts.map((dept) => {
              const s = by_dept[dept];
              const barPct = maxAvg > 0 ? (s.avg_ltv / maxAvg) * 100 : 0;
              return (
                <div key={dept}>
                  <div className="flex justify-between items-baseline mb-1.5 flex-wrap gap-x-4 gap-y-0.5">
                    <span className="text-foreground text-sm font-medium">{dept}</span>
                    <div className="flex gap-3 items-baseline flex-wrap">
                      <span className="text-muted-foreground text-[13px]">{fmt(s.count)} kunder</span>
                      <span className="text-muted-foreground text-[13px]">{s.once_only_pct}% engangskunder</span>
                      <span className="text-foreground text-sm font-bold">{fmt(s.avg_ltv)} kr</span>
                    </div>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#FF2A6D]"
                      style={{ width: `${barPct}%`, transition: "width 0.6s ease" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
